import type * as React from "react";
import { cn } from "@/shared/lib/utils";
import {
	type HeadingVariantProps,
	headingVariants,
	type TextVariantProps,
	textVariants,
} from "./typography-variants";

/**
 * Canonical typography primitives — the single home for the branded type scale.
 *
 * Call sites say what a piece of text *is* ("a card title", "a caption")
 * instead of hand-picking `text-2xl font-semibold tracking-[-0.02em]` per
 * component. Variants + scale rationale live in ./typography-variants.ts.
 */

type HeadingElement = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

export type HeadingProps = React.ComponentPropsWithoutRef<"h2"> &
	HeadingVariantProps & {
		/** Rendered element. Defaults to `h2`; set for correct document outline. */
		as?: HeadingElement;
	};

export function Heading({
	as: Tag = "h2",
	variant,
	className,
	...props
}: HeadingProps) {
	const Component = Tag as React.ElementType;
	return (
		<Component
			className={cn(headingVariants({ variant }), className)}
			{...props}
		/>
	);
}

export type TextProps = React.HTMLAttributes<HTMLElement> &
	Pick<React.LabelHTMLAttributes<HTMLLabelElement>, "htmlFor"> &
	TextVariantProps & {
		/** Rendered element. Defaults to `p`; use `span` for inline text or
		    `label` (with `htmlFor`) for form labels. */
		as?: "p" | "span" | "div" | "label" | "figcaption";
	};

export function Text({
	as: Tag = "p",
	variant,
	tone,
	className,
	...props
}: TextProps) {
	const Component = Tag as React.ElementType;
	return (
		<Component
			className={cn(textVariants({ variant, tone }), className)}
			{...props}
		/>
	);
}
